<template>
  <div class="modal-overlay center" @click.stop>
    <div class="modal-card col" role="dialog" aria-modal="true" aria-labelledby="upgrade-title" aria-describedby="upgrade-desc">
      <div class="header center between">
        <div class="brand center">
          <div class="badge">Pro</div>
          <div class="title" id="upgrade-title">{{ t('upgrade.title') }}</div>
        </div>
      </div>

      <div class="body">
        <p class="desc" id="upgrade-desc">{{ t('upgrade.description', { price: priceDisplay }) }}</p>

        <div v-if="errorMessage" class="mt-15 mb-5 pa-10 shade2 curve border red--text font-14" role="alert" aria-live="assertive">
          {{ errorMessage }}
        </div>

        <div class="plan-row center between gap-30">
          <div class="plan-info col">
            <div class="plan-name">{{ brandName }} Pro</div>
            <ul class="features">
              <li>Up to 30 participants per room</li>
              <li>Peer‑to‑Peer, end‑to‑end encryption</li>
              <li>Priority support</li>
            </ul>
          </div>
          <div class="price-tag center col">
            <div class="amount">{{ price }}</div>
            <div class="currency">{{ currency }}</div>
            <div class="per">/mo</div>
          </div>
        </div>
      </div>

      <div class="actions d-flex center">
        <s-btn class="ghost mr-10 grow" @click="onLogout">{{ t('upgrade.logout') }}</s-btn>
        <s-btn class="primary grow" :disabled="isLoading" @click="onUpgrade">
          {{ isLoading ? 'Processing...' : t('upgrade.upgrade_now') }}
        </s-btn>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, ref, watch } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import router from '@/router';
import { get, post } from '@/assets/functions';
import { getBrandName } from '@/assets/js/whiteLabel';

const store = useStore();
const { t } = useI18n();

const currency = computed(() => store.state.user.entity_currency || 'USD');
const partnerPricingLabel = ref('');
const price = computed(() => {
  const source = partnerPricingLabel.value || '';
  const match = source.match(/([0-9]+(?:\.[0-9]+)?)/);
  if (match && match[1]) {
    return match[1];
  }
  return String(store.state.user.productPrice || '17.95');
});
const priceDisplay = computed(() => partnerPricingLabel.value || `${price.value} ${currency.value}`);
const brandName = computed(() => getBrandName());
const errorMessage = ref('');
const isLoading = ref(false);

const showError = (message) => {
  errorMessage.value = message;
  setTimeout(() => {
    if (errorMessage.value === message) {
      errorMessage.value = '';
    }
  }, 10000);
};

const clearError = () => {
  errorMessage.value = '';
};

const loadPartnerPricing = async () => {
  partnerPricingLabel.value = '';
  try {
    const data = await get('/stripe/checkout-pricing');
    if (data && data.status === 'ok' && data.subscriptionPriceDisplay) {
      partnerPricingLabel.value = data.subscriptionPriceDisplay;
    }
  } catch {
  }
};

const onUpgrade = async () => {
  if (isLoading.value) return;
  isLoading.value = true;
  clearError();

  try {
    const response = await post("/stripe/create-checkout-session", {
      successUrl: window.location.origin + '/',
      cancelUrl: window.location.origin + '/'
    });

    if (response && response.url) {
      window.location.href = response.url;
      return;
    }

    if (response && response.status === 'success' && response.checkoutUrl) {
      window.location.href = response.checkoutUrl;
      return;
    }

    if (response?.message === 'Price not found') {
      showError('Pricing configuration error. Please contact support.');
    } else if (response?.message === 'Customer already has active subscription') {
      showError('You already have an active subscription. Please manage it from the account menu.');
    } else if (response?.message === 'Invalid price ID') {
      showError('Invalid pricing configuration. Please contact support.');
    } else if (response?.statusCode) {
      if (response.statusCode === 404) {
        showError('Checkout service not available. Please contact support.');
      } else if (response.statusCode === 500) {
        showError('Server error. Please try again later or contact support.');
      } else if (response.statusCode === 401) {
        showError('Authentication required. Please log in again.');
      } else if (response.statusCode === 403) {
        showError('Access denied. Please contact support if you believe this is an error.');
      } else {
        showError(`Server error (${response.statusCode}): ${response.message}`);
      }
    } else {
      showError(response?.message || 'Failed to create checkout session');
    }
  } catch (error) {
    console.error('Error creating checkout session:', error);
    if (error.message.includes('Network Error') || error.message.includes('fetch')) {
      showError('Network error: Please check your internet connection and try again.');
    } else if (error.message.includes('timeout')) {
      showError('Request timed out. Please try again.');
    } else if (error.message.includes('Unexpected token')) {
      showError('Server returned invalid response. Please contact support.');
    } else if (error.message.includes('Customer already has active subscription')) {
      showError('You already have an active subscription. Please manage it from the account menu.');
    } else {
      showError('Failed to start upgrade: ' + error.message);
    }
  } finally {
    isLoading.value = false;
  }
};

const onLogout = async () => {
  await store.dispatch('user/logout');
  await store.dispatch('calendar/clearUpcomingEvents');
  store.commit("modals/upgradeRequiredModal", false);
  router.push('/login');
}

loadPartnerPricing();
</script>

<style scoped>
.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,0.45);
  backdrop-filter: blur(4px);
  z-index: 10;
  padding: 20px;
}
.modal-card {
  width: 100%;
  max-width: 520px;
  background: var(--shade1);
  border-radius: 16px;
  box-shadow: 0 18px 50px rgba(0,0,0,0.25);
  padding: 24px;
  border: 1px solid rgba(255,255,255,0.06);
}
.brand {
  gap: 12px;
}
.badge {
  font-size: 11px;
  font-weight: 700;
  letter-spacing: .6px;
  padding: 6px 10px;
  border-radius: 999px;
  color: #fff;
  background: linear-gradient(135deg, var(--primary), #2e9a8a);
  box-shadow: 0 6px 14px rgba(0,0,0,0.18);
}
.header .title {
  font-weight: 800;
  font-size: 22px;
}
.body .desc {
  opacity: .9;
  line-height: 1.6;
}
.plan-row {
  margin-top: 18px;
  padding: 16px;
  border-radius: 12px;
  background: rgba(0,0,0,0.04);
}
.plan-info .plan-name {
  font-weight: 700;
  margin-bottom: 6px;
}
.features {
  margin: 0;
  padding-left: 18px;
}
.features li {
  margin: 4px 0;
  opacity: .9;
}
.price-tag {
  min-width: 120px;
  background: var(--shade1);
  border: 1px solid rgba(0,0,0,0.06);
  border-radius: 12px;
  padding: 10px 14px;
}
.price-tag .amount {
  font-size: 26px;
  font-weight: 800;
}
.price-tag .currency {
  font-size: 12px;
  opacity: .85;
}
.price-tag .per {
  font-size: 12px;
  opacity: .7;
}
.actions .primary {
  background: var(--primary);
  color: #fff;
}
.actions {
  padding-top: 32px;
}
.actions .ghost {
  background: transparent;
  color: var(--black);
}
.actions .primary:hover {
  filter: brightness(0.95);
}
.actions .ghost:hover {
  text-decoration: underline;
}
</style>

